import { useEffect, useState } from "react";
import { ApiError } from "../../shared/api/client";
import type { MeetingPointView, WorkItemType } from "../../shared/types/api";
import { Button, Drawer, Field, InlineAlert, Select, Textarea } from "../../shared/ui";
import { TYPE_LABELS } from "../workitems/workItemVocabulary";
import { MAKEABLE_TYPES } from "./meetingVocabulary";

export function CancelMeetingDrawer({
  open,
  onClose,
  onCancel,
}: {
  open: boolean;
  onClose: () => void;
  onCancel: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
    }
  }, [open]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onCancel(reason.trim());
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not cancel the meeting.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Cancel meeting"
      subtitle="Everyone invited is told, with your reason."
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Keep it
          </Button>
          <Button variant="danger" disabled={!reason.trim()} loading={saving} onClick={() => void submit()}>
            Cancel meeting
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}
        <Field label="Why" required>
          <Textarea rows={3} maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Drawer>
  );
}

/**
 * Turns a point into a work item. The type is asked for because there is no neutral one: a
 * technical task goes through review and QA, a demo preparation needs its screenshot.
 */
export function MakeWorkItemDrawer({
  point,
  onClose,
  onMake,
}: {
  point: MeetingPointView | null;
  onClose: () => void;
  onMake: (type: WorkItemType) => Promise<void>;
}) {
  const [type, setType] = useState<WorkItemType>("TECHNICAL_TASK");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (point) {
      setType("TECHNICAL_TASK");
      setError(null);
    }
  }, [point]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await onMake(type);
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not make the work item.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={point !== null}
      onClose={onClose}
      title="Make a work item"
      subtitle={point?.body}
      footer={
        <div className="flex justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void submit()}>
            Make work item
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}
        <Field
          label="Type"
          hint="The action's owner is assigned. Its due date is quoted in the description — plan the item to set its own dates."
        >
          <Select value={type} onChange={(e) => setType(e.target.value as WorkItemType)}>
            {MAKEABLE_TYPES.map((value) => (
              <option key={value} value={value}>
                {TYPE_LABELS[value]}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Drawer>
  );
}
